import { useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useEffectOnce, useSecondOrderCallback } from '@chilecompra/react-kit'

import { REACT_APP_MERCADO_PUBLICO_BASE_URL } from '../../config/settings/environment.config'
import { onShowSnackbar } from '../SnackbarProvider'
import {
  onAddFileThunk,
  onCloseMoreInfoDialog,
  onCreateCfmpThunk,
  onGetCfmpSourcesThunk,
  onOpenMoreInfoDialog,
  onRemoveFileSuccess,
  onResetCfmp
} from './Cfmp.actions'
import { Container, Item, Root } from './Cfmp.styles'
import { CfmpActions } from './components/CfmpActions'
import { CfmpForm } from './components/CfmpForm'
import { CfmpHeader } from './components/CfmpHeader'
import { CfmpInfoPanel } from './components/CfmpInfoPanel'
import { CfmpMoreInfoDialog } from './components/CfmpMoreInfoDialog'
import { CfmpSuccess } from './components/CfmpSuccess'

const isSameFile = (attached, file) =>
  attached.fileName === file.name && attached.file.size === file.size

/**
 * The Cfmp container.
 */
const Cfmp = () => {
  const dispatch = useDispatch()
  const formRef = useRef(null)

  const {
    administrativeActTypes,
    attachedFiles,
    currencies,
    procedureTypes,
    reasonTypes,
    showMoreInfoDialog,
    submit
  } = useSelector(state => state.cfmp)

  const isSourcesLoading =
    currencies.loading ||
    administrativeActTypes.loading ||
    procedureTypes.loading ||
    reasonTypes.loading

  const isFormDisabled = isSourcesLoading || submit.loading

  const notify = useCallback(
    (message, severity = 'warning') => dispatch(onShowSnackbar({ message, severity })),
    [dispatch]
  )

  const handleBack = useCallback(() => {
    window.location.href = `${REACT_APP_MERCADO_PUBLICO_BASE_URL}/Portal/Modules/Menu/Menu.aspx`
  }, [])

  const handleAddFile = useCallback(
    (categoryId, file) => {
      const categoryFiles = attachedFiles.filter(attached => attached.categoryId === categoryId)

      if (categoryFiles.some(attached => isSameFile(attached, file))) {
        return notify('El archivo ya fue adjuntado.')
      }

      dispatch(onAddFileThunk({ categoryId, file }))
    },
    [attachedFiles, notify, dispatch]
  )

  const handleCloseMoreInfoDialog = useCallback(() => dispatch(onCloseMoreInfoDialog()), [dispatch])

  const handleOpenMoreInfoDialog = useCallback(() => dispatch(onOpenMoreInfoDialog()), [dispatch])

  const handleRemoveFile = useSecondOrderCallback((_, id) => dispatch(onRemoveFileSuccess({ id })))

  const handleResetCfmp = useCallback(() => dispatch(onResetCfmp()), [dispatch])

  const handleSubmit = useCallback(() => {
    if (!formRef.current?.validate()) return

    dispatch(
      onCreateCfmpThunk({
        attachedFiles,
        callbackFormReset: () => formRef.current.reset(),
        cfmp: formRef.current.getValues()
      })
    )
  }, [attachedFiles, dispatch])

  useEffectOnce(() => dispatch(onGetCfmpSourcesThunk()))

  return (
    <Root direction="column" spacing={4}>
      {!submit.success && (
        <>
          <CfmpHeader />

          <Container spacing={4}>
            <Item className="form" size={{ lg: 9, xs: 12 }}>
              <CfmpForm
                attachedFiles={attachedFiles}
                isDisabled={isFormDisabled}
                isSourcesLoading={isSourcesLoading}
                onAddFile={handleAddFile}
                onOpenMoreInfoDialog={handleOpenMoreInfoDialog}
                onRemoveFile={handleRemoveFile}
                ref={formRef}
                sources={{
                  administrativeActTypes,
                  currencies,
                  procedureTypes,
                  reasonTypes
                }}
              />
            </Item>

            <Item className="info-panel" size={{ lg: 3, xs: 12 }}>
              <CfmpInfoPanel />
            </Item>
          </Container>

          <CfmpActions
            isDisabled={isFormDisabled}
            onBack={handleBack}
            onSubmit={handleSubmit}
            submit={submit}
          />
        </>
      )}

      {submit.success && <CfmpSuccess cfmp={submit.cfmp} onResetCfmp={handleResetCfmp} />}

      <CfmpMoreInfoDialog
        onCloseMoreInfoDialog={handleCloseMoreInfoDialog}
        showMoreInfoDialog={showMoreInfoDialog}
      />
    </Root>
  )
}

export default Cfmp
